import React from 'react';

const mToFt = (m) => (m * 3.28084).toFixed(1);
const cToF = (c) => (c * 9 / 5 + 32).toFixed(0);

function Results({ results, loading }) {
  if (loading) {
    return (
      <div className="card results-card">
        <div className="card-header">
          <div className="card-header-icon results">R</div>
          <h2>Results</h2>
        </div>
        <div className="card-body">
          <div className="loading">
            <div className="loading-spinner" />
            <div>Calculating...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="card results-card">
        <div className="card-header">
          <div className="card-header-icon results">R</div>
          <h2>Results</h2>
        </div>
        <div className="card-body">
          <div className="empty-results">
            <div className="empty-icon">((( )))</div>
            <h3>Ready to Calculate</h3>
            <p>Set your source, site, and compliance parameters, then click Calculate.</p>
          </div>
          <div className="methodology-note" style={{ marginTop: '1.5rem' }}>
            <strong>Method:</strong> ISO 9613-2:1996 outdoor sound propagation.
            Calculates geometric divergence, atmospheric absorption (ISO 9613-1),
            and ground effect. Conservative screening using 500 Hz representative
            atmospheric coefficient for A-weighted input.
          </div>
          <div className="cta-section">
            <h3>Need a Full Noise Study?</h3>
            <p>
              Multi-source analysis, octave-band modeling, sound contour maps,
              and PE-stamped reports for permitting.
            </p>
            <a className="cta-btn" href="https://franceng.com/contact/" target="_blank" rel="noopener noreferrer"
              onClick={() => { window.location.href = 'mailto:caseym@franceng.com?subject=BESS%20Noise%20Study%20Inquiry'; }}
            >
              Contact Franc Engineering
            </a>
          </div>
        </div>
      </div>
    );
  }

  const { calculation, inputs } = results;
  const noiseLimit = inputs._noise_limit;
  const predicted = calculation.sound_pressure_level;
  const margin = noiseLimit != null ? (noiseLimit - predicted).toFixed(1) : null;
  const compliant = noiseLimit != null ? predicted <= noiseLimit : null;

  return (
    <div className="card results-card">
      <div className="card-header">
        <div className="card-header-icon results">R</div>
        <h2>Results</h2>
      </div>
      <div className="card-body">
        {/* Hero Result */}
        <div className="result-hero">
          <div>
            <span className="result-value">{predicted}</span>
            <span className="result-unit">dBA</span>
          </div>
          <div className="result-label">Predicted Sound Pressure Level at Receiver</div>

          {noiseLimit != null && (
            <div className="compliance-result">
              {compliant ? (
                <span className="compliance-badge pass">
                  <span className="badge-icon">&#10003;</span>
                  COMPLIANT &mdash; {margin} dB margin
                </span>
              ) : (
                <span className="compliance-badge fail">
                  <span className="badge-icon">&#10007;</span>
                  EXCEEDS LIMIT by {Math.abs(margin)} dB
                </span>
              )}
            </div>
          )}
        </div>

        {/* Attenuation Breakdown */}
        <div className="data-section">
          <div className="data-section-title">Attenuation Breakdown</div>
          <table className="data-table">
            <tbody>
              <tr>
                <td>Geometric divergence (A<sub>div</sub>)</td>
                <td>{calculation.attenuation.geometric_divergence} dB</td>
              </tr>
              <tr>
                <td>Atmospheric absorption (A<sub>atm</sub>)</td>
                <td>{calculation.attenuation.atmospheric_absorption} dB</td>
              </tr>
              <tr>
                <td>Ground effect (A<sub>gr</sub>)</td>
                <td>{calculation.attenuation.ground_effect} dB</td>
              </tr>
              {calculation.attenuation.barrier > 0 && (
                <tr>
                  <td>Barrier (A<sub>bar</sub>)</td>
                  <td>{calculation.attenuation.barrier} dB</td>
                </tr>
              )}
              <tr className="total-row">
                <td>Total attenuation</td>
                <td>{calculation.attenuation.total} dB</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Input Summary */}
        <div className="data-section">
          <div className="data-section-title">Input Summary</div>
          <table className="data-table">
            <tbody>
              <tr>
                <td>Source LW</td>
                <td>{calculation.sound_power_level} dBA</td>
              </tr>
              <tr>
                <td>Distance</td>
                <td>{calculation.distance} m ({mToFt(calculation.distance)} ft)</td>
              </tr>
              <tr>
                <td>Source height</td>
                <td>{calculation.parameters.source_height} m ({mToFt(calculation.parameters.source_height)} ft)</td>
              </tr>
              <tr>
                <td>Receiver height</td>
                <td>{calculation.parameters.receiver_height} m ({mToFt(calculation.parameters.receiver_height)} ft)</td>
              </tr>
              <tr>
                <td>Ground factor</td>
                <td>G = {calculation.parameters.ground_factor}</td>
              </tr>
              <tr>
                <td>Temperature / Humidity</td>
                <td>{calculation.parameters.temperature_c}{'\u00B0'}C ({cToF(calculation.parameters.temperature_c)}{'\u00B0'}F) / {calculation.parameters.relative_humidity}%</td>
              </tr>
              {noiseLimit != null && (
                <tr>
                  <td>Noise limit</td>
                  <td>{noiseLimit} dBA</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Equation */}
        <div className="methodology-note">
          <strong>Lp</strong> = {calculation.sound_power_level} &minus; {calculation.attenuation.total}
          {calculation.meteorological_correction > 0 && <> + {calculation.meteorological_correction}</>}
          {' '}= <code>{predicted} dBA</code>
          <br />
          <span style={{ opacity: 0.8 }}>
            ISO 9613-2 | A-weighted simplified mode | Omnidirectional source | No barriers
          </span>
        </div>

        {/* CTA */}
        <div className="cta-section">
          <h3>Need a PE-Stamped Study?</h3>
          <p>
            This is a screening-level estimate. For multi-source modeling,
            octave-band analysis, contour maps, and PE certification &mdash;
            contact Franc Engineering.
          </p>
          <a className="cta-btn" href="https://franceng.com/contact/" target="_blank" rel="noopener noreferrer"
            onClick={() => { window.location.href = 'mailto:caseym@franceng.com?subject=BESS%20Noise%20Study%20Inquiry'; }}
          >
            Contact Franc Engineering
          </a>
        </div>
      </div>
    </div>
  );
}

export default Results;
